import React from "react";
import { Helmet } from "react-helmet";
import { connect } from "react-redux";
import { push } from "react-router-redux";
import * as WebRoutes from "@travellocal/utils/webRoutes"
import { RoutesInvoker } from "../web/routesInvoker";
import { EnterHookPromises } from "../web/state/enterHookPromises";
import { LocationImageGalleryDialog } from "./components/locationImageGalleryDialog";
import { apiPromiseHook } from "./flux/actions/enterHooks";
import { GoogleAnalyticsHelper } from "@travellocal/ui";

interface IDialogParams {
  enquiryId: number;
  fileName: string;
  itineraryQuoteId: number;
}

interface OwnProps {
  params: IDialogParams;
}

interface StateProps {
  sampleItinerary: IState<IItinerarySummary>;
  customerItinerary: IKeyedState<IState<IEnquiryItinerarySummary>>;
  currentLocationImage: IState<IAccomodationLocationImage>;
}

interface DispatchProps {
  backToSampleItinerary: (itinerarySlug: string) => void;
  backToCustomerItinerary: (enquiryId: number, quoteId: number) => void;
  imageClicked: (itinerarySlug: string, fileName: string, isSampleItinerary: boolean) => void;
}

type Props = StateProps & OwnProps & DispatchProps;

interface State {
  galleryImages: IAccomodationLocationImage[];
}

class ItinerarySummaryGalleryDialogComponent extends React.Component<Props, State> {
  constructor(props) {
    super(props);
    this.state = {
      galleryImages: [],
    };
  }

  private onGalleryClose(isSampleItinerary: boolean, slug: string): void {
    GoogleAnalyticsHelper.logEvent("Image Gallery", "Click", "Close Gallery");
    if (isSampleItinerary) {
      this.props.backToSampleItinerary(slug);
    } else {
      this.props.backToCustomerItinerary(
        this.props.params.enquiryId,
        this.props.params.itineraryQuoteId,
      );
    }
  }

  // Itinerary images don't have descriptions so use name and description from itinerary component
  private imageDescription = component => component ? `${component.name}\n${component.description}` : null;

  public componentDidMount() {
    const { enquiryId, itineraryQuoteId } = this.props.params;
    const isSampleItinerary = enquiryId == null;
    const itin = isSampleItinerary
      ? this.props.sampleItinerary.data
      : this.props.customerItinerary.data[itineraryQuoteId].data;
    const images: IAccomodationLocationImage[] = [];

    // Iterate over days in the itinerary and extract event and accomodation images in order for each day
    itin.days.map(day => {
      day.events.map(event => {
        event.images.map(eventImage => {
          eventImage.description = this.imageDescription(event);
          images.push(eventImage);
        });
      });
      if (day.dayAccommodation) {
        day.dayAccommodation.images.map(accomImage => {
          accomImage.description = this.imageDescription(day.dayAccommodation);
          images.push(accomImage);
        });
      }
    });

    this.setState({
      galleryImages: images,
    });
  }

  public render() {
    const { enquiryId, fileName, itineraryQuoteId } = this.props.params;
    const isSampleItinerary = enquiryId == null;
    const itin = isSampleItinerary
      ? this.props.sampleItinerary.data
      : this.props.customerItinerary.data[itineraryQuoteId].data;
    const slug = isSampleItinerary
      ? (itin as ISampleItinerarySummary).slug
      : `enquiries/${(itin as IEnquiryItinerarySummary).enquiryId}/itinerary/${itineraryQuoteId}`;

    if (this.state.galleryImages.length === 0) {
      return null;
    }

    return (
      <div>
        {this.props.currentLocationImage.data &&
          <Helmet
            title={this.props.currentLocationImage.data.meta}
            meta={[
              { name: "description", content: this.props.currentLocationImage.data.description },
            ]}
          />
        }

        <LocationImageGalleryDialog
          isOpen={true}
          selectedImageFileName={fileName}
          onGalleryClosed={() => this.onGalleryClose(isSampleItinerary, slug)}
          prevClicked={(im) => this.props.imageClicked(slug, im.fileName, isSampleItinerary)}
          nextClicked={(im) => this.props.imageClicked(slug, im.fileName, isSampleItinerary)}
          imageClicked={(im) => this.props.imageClicked(slug, im.fileName, isSampleItinerary)}
          title={isSampleItinerary ? `${itin.name} Image Gallery` : "Itinerary Image Gallery"}
          images={this.state.galleryImages}
        />
      </div>
    );
  }
}

const mapDispatchToProps = dispatch => {
  return {
    backToSampleItinerary: (itinerarySlug: string) => dispatch(push(WebRoutes.sampleItinerary(itinerarySlug))),
    backToCustomerItinerary: (enquiryId: number, quoteId: number) => dispatch(push(WebRoutes.customerEnquiryItinerary(enquiryId, quoteId))),
    imageClicked: (itinerarySlug: string, fileName: string, isSampleItinerary: boolean) =>
      dispatch(push(RoutesInvoker.itineraryGalleryImage(itinerarySlug, fileName, isSampleItinerary))),
  };
};

const mapStateToProps = (state: IWebStoreState): StateProps => {
  return {
    sampleItinerary: state.sampleItinerary,
    customerItinerary: state.customerItineraries,
    currentLocationImage: state.currentLocationImage,
  };
};

export const ItinerarySummaryGalleryDialog = connect<StateProps, DispatchProps, OwnProps>(mapStateToProps, mapDispatchToProps)(ItinerarySummaryGalleryDialogComponent);

export const itinerarySummaryGalleryDialogOnEnter = apiPromiseHook<any>(EnterHookPromises.imageByFileName("fileName"));
